<script setup lang="ts" generic="T">
import { computed, ref, watch, onMounted } from 'vue'
import emblaCarouselVue from 'embla-carousel-vue'
import Autoplay from 'embla-carousel-autoplay'
import type { EmblaOptionsType } from 'embla-carousel'

interface Props {
  autoHeight?: boolean
  autoPlay?: boolean
  direction?: 'horizontal' | 'vertical'
  gap?: string
  items: T[]
  loop?: boolean
  options?: EmblaOptionsType
  padding?: string | number
  showControls?: boolean
  slideClass?: string
  slidesToShow?: {
    desktop?: number
    mobile?: number
    tablet?: number
  }
}

const DEFAULT_SLIDES_TO_SHOW: Props['slidesToShow'] = {
  desktop: 3,
  mobile: 1,
  tablet: 2,
}

const props = withDefaults(defineProps<Props>(), {
  autoHeight: true,
  autoPlay: false,
  direction: 'horizontal',
  gap: '0.5rem',
  loop: false,
  options: () => ({}),
  padding: '0',
  showControls: true,
  slideClass: '',
  slidesToShow: () => ({
    desktop: 3,
    mobile: 1,
    tablet: 2,
  }),
})

// Computed properties
const isVertical = computed(() => props.direction === 'vertical')

const emblaOptions = computed(
  (): EmblaOptionsType => ({
    axis: isVertical.value ? 'y' : 'x',
    loop: props.loop,
    align: 'start',
    containScroll: 'trimSnaps',
    ...props.options,
  }),
)

const slidesOnScreen = computed(() => {
  return {
    ...DEFAULT_SLIDES_TO_SHOW,
    ...props.slidesToShow,
  }
})

const controlsClass = computed(() => ({
  'ue-carousel__controls--vertical': isVertical.value,
  'ue-carousel__controls--horizontal': !isVertical.value,
}))

const prevIconClass = computed(() => (isVertical.value ? 'bi bi-chevron-up' : 'bi bi-chevron-left'))

const nextIconClass = computed(() =>
  isVertical.value ? 'bi bi-chevron-down' : 'bi bi-chevron-right',
)

// Computed property to show dots only when there are more than 2 slides
const showDots = computed(() => props.items.length > 2)
const showControls = computed(() => props.showControls && props.items.length > 1)
const emblaPlugins = computed(() => {
  const plugins = []

  if (props.autoPlay) {
    plugins.push(
      Autoplay({
        delay: 3000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    )
  }
  return plugins
})

// Embla setup
const [emblaRef, emblaApi] = emblaCarouselVue(emblaOptions, emblaPlugins)

// Reactive state
const canScrollPrev = ref(false)
const canScrollNext = ref(false)
const selectedIndex = ref(0)

// Methods
const scrollPrev = (): void => {
  if (emblaApi.value) {
    emblaApi.value.scrollPrev()
  }
}

const scrollNext = (): void => {
  if (emblaApi.value) {
    emblaApi.value.scrollNext()
  }
}

const scrollTo = (index: number): void => {
  if (emblaApi.value) {
    emblaApi.value.scrollTo(index)
  }
}

const updateScrollButtons = (): void => {
  if (emblaApi.value) {
    canScrollPrev.value = emblaApi.value.canScrollPrev()
    canScrollNext.value = emblaApi.value.canScrollNext()
    selectedIndex.value = emblaApi.value.selectedScrollSnap()
  }
}

// Watch for API changes and setup event listeners
watch(emblaApi, (api) => {
  if (api) {
    updateScrollButtons()
    api.on('select', updateScrollButtons)
    api.on('reInit', updateScrollButtons)
  }
})

onMounted(() => {
  if (emblaApi.value) {
    updateScrollButtons()
  }
})
</script>

<template>
  <div class="ue-carousel w-100 position-relative">
    <div class="embla w-100 overflow-hidden" ref="emblaRef">
      <div class="embla__container d-flex" :class="{ 'embla__container--vertical': isVertical }">
        <div v-for="(item, index) in items" :key="index" class="embla__slide" :class="slideClass">
          <slot name="slide" :item="item" :index="index">
            <!-- Default slot content if no template provided -->
            <div
              class="default-slide d-flex align-items-center justify-content-center text-center rounded"
            >
              {{ item }}
            </div>
          </slot>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div
      v-if="showControls"
      class="ue-carousel__controls d-flex justify-content-center align-items-center"
      :class="controlsClass"
    >
      <button
        class="d-flex align-items-center justify-content-center rounded-circle border-0 ue-carousel__control ue-carousel__control--prev"
        @click="scrollPrev"
        :disabled="!canScrollPrev"
        type="button"
        aria-label="Previous slide"
      >
        <i :class="prevIconClass"></i>
      </button>

      <!-- Dot indicators -->
      <div v-if="showDots" class="ue-carousel__dots d-flex align-items-center">
        <button
          v-for="(_item, index) in items"
          :key="index"
          class="ue-carousel__dot border-0 rounded-circle p-0 m-0"
          :class="{ 'ue-carousel__dot--active': selectedIndex === index }"
          @click="scrollTo(index)"
          :aria-label="`Go to slide ${index + 1}`"
          type="button"
        />
      </div>

      <button
        class="d-flex align-items-center justify-content-center rounded-circle border-0 ue-carousel__control ue-carousel__control--next"
        @click="scrollNext"
        :disabled="!canScrollNext"
        type="button"
        aria-label="Next slide"
      >
        <i :class="nextIconClass"></i>
      </button>
    </div>
  </div>
</template>

<style scoped lang="sass">
.ue-carousel
  max-width: 100%
  overflow: hidden

.embla
  max-width: 100%

.embla__container
  &--vertical
    flex-direction: column
    height: 300px // Default height for vertical carousel

    .embla__slide
      margin: v-bind('props.gap') 0

.embla__slide
  min-width: 0
  min-height: 0
  transform: translate3d(0, 0, 0)
  padding: v-bind('props.padding')
  flex: 0 0 calc(100% / v-bind('slidesOnScreen.mobile'))
  margin: 0 0.25rem

  // Tablet breakpoint
  @media (min-width: 576px)
    flex: 0 0 calc(100% / v-bind('slidesOnScreen.tablet'))
    margin: 0 v-bind('props.gap')

  // Desktop breakpoint
  @media (min-width: 992px)
    flex: 0 0 calc(100% / v-bind('slidesOnScreen.desktop'))

.ue-carousel__controls
  margin-top: 2.5rem
  gap: 1rem

  &--vertical
    margin-bottom: 0

.ue-carousel__dots
  gap: 0.5rem

.ue-carousel__dot
  width: 0.5rem
  height: 0.5rem
  background-color: var(--ue-gray-container-bg)
  cursor: pointer
  transition: all 0.2s ease-in-out

  &:hover
    background-color: var(--paginationColor)
    transform: scale(1.2)

  &--active
    background-color: var(--paginationColor)
    transform: scale(1.3)

  &:focus
    outline: none

.ue-carousel__control
  background-color: var(--ue-gray-container-bg)
  color: var(--ue-gray-container-color)
  cursor: pointer
  font-size: 0.75rem
  height: 1.875rem
  transition: all 0.2s ease-in-out
  width: 1.875rem

  &:hover:not(:disabled)
    background-color: #f8f9fa
    color: #495057
    transform: scale(1.05)

  &:active:not(:disabled)
    transform: scale(0.95)

  &:disabled
    opacity: 0.5
    cursor: not-allowed
    transform: none

  &:focus
    outline: none
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25)

.default-slide
  padding: 1rem
  min-height: 100px
</style>
